import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Html5QrcodeScanner, Html5Qrcode } from 'html5-qrcode';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
    ArrowLeft, 
    QrCode, 
    CheckCircle2, 
    XCircle, 
    Users, 
    Ticket,
    RefreshCw,
    Camera,
    CameraOff,
    List,
    Scan
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckInScanner = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [stats, setStats] = useState(null);
    const [scanResult, setScanResult] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const [showList, setShowList] = useState(false);
    const [attendees, setAttendees] = useState([]);
    const [error, setError] = useState(null);
    const scannerRef = useRef(null);
    const html5QrcodeRef = useRef(null);

    useEffect(() => {
        fetchEvent();
        fetchStats();
        fetchAttendees();
        
        return () => {
            stopScanner();
        };
    }, [eventId]);

    const fetchEvent = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/events/${eventId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEvent(response.data);
        } catch (err) {
            console.error('Error fetching event:', err);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/checkin/stats/${eventId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(response.data);
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    };

    const fetchAttendees = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/checkin/attendees/${eventId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAttendees(response.data);
        } catch (err) {
            console.error('Error fetching attendees:', err);
        }
    };

    const startScanner = async () => {
        try {
            setScanResult(null);
            setError(null);
            setIsScanning(true);
            
            const html5Qrcode = new Html5Qrcode("qr-reader");
            html5QrcodeRef.current = html5Qrcode;
            
            await html5Qrcode.start(
                { facingMode: "environment" },
                {
                    fps: 10,
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1.0
                },
                onScanSuccess,
                onScanFailure
            );
        } catch (err) {
            console.error("Scanner error:", err);
            setError("Kamera konnte nicht gestartet werden. Bitte Berechtigung erteilen.");
            setIsScanning(false);
        }
    };

    const stopScanner = async () => {
        if (html5QrcodeRef.current) {
            try {
                await html5QrcodeRef.current.stop();
                html5QrcodeRef.current = null;
            } catch (err) {
                console.error("Error stopping scanner:", err);
            }
        }
        setIsScanning(false);
    };

    const onScanSuccess = async (decodedText) => {
        // Stop scanner immediately after successful scan
        await stopScanner();
        
        // Vibrate on mobile
        if (navigator.vibrate) {
            navigator.vibrate(100);
        }
        
        // Process the scanned QR code
        await processCheckIn(decodedText);
    };

    const onScanFailure = (error) => {
        // Ignore scan failures (they happen frequently when no QR code is in view)
    };

    const processCheckIn = async (qrData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_URL}/api/checkin/scan`,
                { qr_data: qrData },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setScanResult(response.data);
            
            // Refresh stats and attendees
            fetchStats();
            fetchAttendees();
            
        } catch (err) {
            console.error('Check-in error:', err);
            setScanResult({
                success: false,
                message: err.response?.data?.detail || 'Fehler beim Check-in'
            });
        }
    };

    const handleManualCheckIn = async (orderId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_URL}/api/checkin/manual/${orderId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Refresh data
            fetchStats();
            fetchAttendees();
            
        } catch (err) {
            console.error('Manual check-in error:', err);
            alert(err.response?.data?.detail || 'Fehler beim Check-in');
        }
    };

    const handleResetCheckIn = async (orderId) => {
        if (!window.confirm('Check-in wirklich zurücksetzen?')) return;
        
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_URL}/api/checkin/reset/${orderId}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Refresh data
            fetchStats();
            fetchAttendees();
            
        } catch (err) {
            console.error('Reset check-in error:', err);
        }
    };

    const scanAgain = () => {
        setScanResult(null);
        startScanner();
    };

    return (
        <div className="min-h-screen bg-black text-white">
            {/* Header */}
            <div className="sticky top-0 z-50 bg-black border-b border-gray-800 px-4 py-3">
                <div className="flex items-center justify-between max-w-lg mx-auto">
                    <button 
                        onClick={() => navigate('/admin/events')}
                        className="flex items-center gap-2 text-gray-400 hover:text-white"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span className="hidden sm:inline">Zurück</span>
                    </button>
                    <div className="text-center flex-1 px-4">
                        <h1 className="text-lg font-bold truncate">{event?.title || 'Check-in'}</h1>
                    </div>
                    <button
                        onClick={() => setShowList(!showList)}
                        className={`p-2 rounded-lg transition-colors ${showList ? 'bg-white text-black' : 'bg-gray-800 text-white'}`}
                    >
                        {showList ? <Scan className="w-5 h-5" /> : <List className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {/* Stats Bar */}
            {stats && (
                <div className="bg-gray-900 px-4 py-3 border-b border-gray-800">
                    <div className="max-w-lg mx-auto flex justify-around">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-400">{stats.checked_in_tickets}</div>
                            <div className="text-xs text-gray-500">Eingecheckt</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold">{stats.total_tickets}</div>
                            <div className="text-xs text-gray-500">Gesamt</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-400">{stats.check_in_percentage}%</div>
                            <div className="text-xs text-gray-500">Quote</div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-lg mx-auto p-4">
                {showList ? (
                    /* Attendee List View */
                    <div className="space-y-2">
                        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Teilnehmerliste ({attendees.length})
                        </h2>
                        
                        {attendees.map((attendee) => (
                            <div 
                                key={attendee.order_id}
                                className={`rounded-xl p-4 transition-all ${
                                    attendee.checked_in 
                                        ? 'bg-green-900/30 border border-green-800' 
                                        : 'bg-gray-900 border border-gray-800'
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            {attendee.checked_in ? (
                                                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                                            ) : (
                                                <div className="w-5 h-5 rounded-full border-2 border-gray-600 flex-shrink-0" />
                                            )}
                                            <span className="font-semibold truncate">{attendee.customer_name}</span>
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1 pl-7">
                                            {attendee.tickets.join(', ')}
                                        </div>
                                        {attendee.checked_in && attendee.checked_in_at && (
                                            <div className="text-xs text-green-500 mt-1 pl-7">
                                                ✓ {new Date(attendee.checked_in_at).toLocaleTimeString('de-DE')}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-shrink-0 ml-2">
                                        {attendee.checked_in ? (
                                            <button
                                                onClick={() => handleResetCheckIn(attendee.order_id)}
                                                className="text-xs text-gray-500 hover:text-red-400 px-2 py-1"
                                            >
                                                Reset
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleManualCheckIn(attendee.order_id)}
                                                className="bg-green-600 hover:bg-green-500 text-white text-sm px-3 py-1.5 rounded-lg font-medium"
                                            >
                                                Check-in
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        {attendees.length === 0 && (
                            <div className="text-center text-gray-500 py-8">
                                Keine Teilnehmer gefunden
                            </div>
                        )}
                    </div>
                ) : (
                    /* Scanner View */
                    <div className="space-y-4">
                        {/* Scan Result */}
                        {scanResult && (
                            <div className={`rounded-2xl p-6 text-center ${
                                scanResult.success 
                                    ? (scanResult.already_checked_in ? 'bg-yellow-900/50' : 'bg-green-900/50')
                                    : 'bg-red-900/50'
                            }`}>
                                {scanResult.success ? (
                                    scanResult.already_checked_in ? (
                                        <div className="text-yellow-400">
                                            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
                                                <CheckCircle2 className="w-12 h-12" />
                                            </div>
                                            <h2 className="text-2xl font-bold mb-2">Bereits eingecheckt!</h2>
                                        </div>
                                    ) : (
                                        <div className="text-green-400">
                                            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center animate-pulse">
                                                <CheckCircle2 className="w-12 h-12" />
                                            </div>
                                            <h2 className="text-2xl font-bold mb-2">✓ Willkommen!</h2>
                                        </div>
                                    )
                                ) : (
                                    <div className="text-red-400">
                                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                                            <XCircle className="w-12 h-12" />
                                        </div>
                                        <h2 className="text-2xl font-bold mb-2">Ungültig!</h2>
                                    </div>
                                )}
                                
                                <p className="text-lg mb-2">{scanResult.message}</p>
                                
                                {scanResult.attendee_name && (
                                    <div className="mt-4 text-white">
                                        <div className="text-xl font-bold">{scanResult.attendee_name}</div>
                                        <div className="text-sm text-gray-400 mt-1">{scanResult.ticket_type}</div>
                                    </div>
                                )}
                                
                                <Button 
                                    onClick={scanAgain}
                                    className="mt-6 bg-white text-black hover:bg-gray-200 w-full py-6 text-lg font-bold rounded-xl"
                                >
                                    <RefreshCw className="w-5 h-5 mr-2" />
                                    Nächster Scan
                                </Button>
                            </div>
                        )}

                        {/* Scanner Area */}
                        {!scanResult && (
                            <div className="space-y-4">
                                {/* QR Reader Container */}
                                <div 
                                    id="qr-reader" 
                                    className={`rounded-2xl overflow-hidden bg-gray-900 ${isScanning ? '' : 'hidden'}`}
                                    style={{ width: '100%' }}
                                />

                                {/* Scanner Controls */}
                                {!isScanning ? (
                                    <div className="text-center space-y-4">
                                        <div className="w-32 h-32 mx-auto rounded-2xl bg-gray-900 flex items-center justify-center">
                                            <QrCode className="w-16 h-16 text-gray-600" />
                                        </div>
                                        
                                        <p className="text-gray-400">
                                            Tippe auf den Button, um den QR-Code Scanner zu starten
                                        </p>
                                        
                                        {error && (
                                            <p className="text-red-400 text-sm">{error}</p>
                                        )}
                                        
                                        <Button 
                                            onClick={startScanner}
                                            className="bg-white text-black hover:bg-gray-200 w-full py-6 text-lg font-bold rounded-xl"
                                        >
                                            <Camera className="w-6 h-6 mr-2" />
                                            Scanner starten
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="text-center space-y-4">
                                        <p className="text-gray-400">
                                            Halte den QR-Code vor die Kamera
                                        </p>
                                        
                                        <Button 
                                            onClick={stopScanner}
                                            variant="outline"
                                            className="border-gray-700 text-gray-400 hover:text-white"
                                        >
                                            <CameraOff className="w-4 h-4 mr-2" />
                                            Scanner stoppen
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Custom styles for html5-qrcode */}
            <style>{`
                #qr-reader {
                    border: none !important;
                }
                #qr-reader video {
                    border-radius: 16px;
                }
                #qr-reader__scan_region {
                    background: transparent !important;
                }
                #qr-reader__dashboard {
                    display: none !important;
                }
                #qr-reader__dashboard_section {
                    display: none !important;
                }
                #qr-reader__dashboard_section_swaplink {
                    display: none !important;
                }
                #qr-reader__status_span {
                    display: none !important;
                }
                #qr-reader__filescan_input {
                    display: none !important;
                }
            `}</style>
        </div>
    );
};

export default CheckInScanner;
